import type { HTMLAttributes, SVGProps } from 'react';

import { cn } from '@/lib/cn';

export type LogoProps = {
  className?: string;
} & SVGProps<SVGSVGElement>;

export const LogoMark = ({ className = 'w-9 h-auto', ...props }: LogoProps) => (
  <svg viewBox="0 0 116.68133 89.793637" role="img" aria-label="Kaibernetic mark" className={className} {...props}>
    <defs>
      <linearGradient id="kmGradBase">
        <stop style={{ stopColor: '#363b3e', stopOpacity: 1 }} offset="0" />
        <stop style={{ stopColor: '#4ea1a2', stopOpacity: 1 }} offset="1" />
      </linearGradient>
      <linearGradient
        xlinkHref="#kmGradBase"
        id="kmGradUse"
        x1="50.835278"
        y1="151.42551"
        x2="149.56122"
        y2="85.675125"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(-27.216913,-48.263628)"
      />
    </defs>
    <g transform="translate(-23.230844,-21.708358)">
      <path
        style={{ fill: 'url(#kmGradUse)', fillOpacity: 1 }}
        d="m 36.996067,111.2425 c -2.47285,0.22235 -6.98045,0.38834 -10.11911,0.12343 -0.14913,-0.0126 -0.0164,-0.40443 0.19863,-0.66358 0.30623,-0.36899 0.145,-0.56247 -0.74348,-0.89217 -1.69908,-0.63052 -4.02179,-4.29816 -2.72201,-4.29816 0.19402,0 0.35277,-0.3175 0.35277,-0.70555 0,-0.44899 0.23519,-0.70556 0.64676,-0.70556 0.96815,0 1.18277,-0.40123 0.49429,-0.92404 -0.53567,-0.40676 -0.41415,-0.46736 0.97562,-0.48658 1.2531,-0.0173 1.45746,-0.0977 0.97014,-0.38152 -0.96581,-0.56253 -0.72714,-1.02956 0.52917,-1.03548 0.63059,-0.003 1.37927,-0.15261 1.66374,-0.33253 0.41289,-0.26113 0.30615,-0.4157 -0.52917,-0.76631 -0.99391,-0.41717 -0.88718,-0.46008 2.12862,-0.85575 1.74625,-0.2291 2.81781,-0.43746 2.38125,-0.463 -0.43656,-0.0256 -0.79375,-0.2052 -0.79375,-0.39923 0,-0.22074 10.63057,-0.99836 14.16624,-1.64101 8.517061,-1.5481 8.530756,-1.9856 16.437233,-4.19518 3.569362,-0.99752 7.592648,-2.354897 9.227188,-3.113077 0.648863,-0.30097 3.103203,-1.40501 5.454093,-2.45341 7.69866,-3.43329 17.58134,-9.71087 23.317809,-14.81172 10.28136,-9.14213 13.86442,-11.18469 21.74733,-21.67778 4.48107,-5.96484 11.01373,-17.199857 14.64901,-25.193712 0.94171,-2.07078 1.75912,-3.71812 1.81647,-3.66077 0.0574,0.0574 -0.3959,1.80014 -1.00724,3.87285 -0.61134,2.07271 -1.03045,3.84964 -0.93135,3.94874 0.0991,0.0991 0.6949,-1.18364 1.32403,-2.85051 0.62912,-1.66688 1.20124,-2.97329 1.27138,-2.90315 0.20014,0.20013 -2.52731,9.291035 -3.6985,12.327542 -0.58047,1.50497 -0.92822,2.65771 -0.77277,2.56164 0.36969,-0.22848 3.23236,-6.753032 3.56275,-8.120172 0.2097,-0.86768 0.26228,-0.9119 0.29188,-0.24548 0.1011,2.275725 -3.85517,13.829142 -6.849,20.001032 -8.50039,17.52381 -21.17094,30.2492 -38.182249,41.998077 -5.64862,3.90122 -13.73122,8.20992 -20.27099,10.80613 -6.835996,3.0732 -14.347813,5.09969 -21.291384,6.30213 -5.96509,1.01069 -9.91834,1.3144 -15.6954,1.83386 z"
      />
    </g>
  </svg>
);

export type LogoWordmarkProps = HTMLAttributes<HTMLSpanElement>;

export const LogoWordmark = ({ className, ...props }: LogoWordmarkProps) => (
  <span
    {...props}
    className={cn(
      'text-[1.65rem] font-semibold tracking-[0.08em] text-text-primary lowercase leading-none',
      className,
    )}
  >
    kaibernetic
  </span>
);

export interface LogoLockupProps extends HTMLAttributes<HTMLDivElement> {
  markClassName?: string;
  wordmarkClassName?: string;
}

export const LogoLockup = ({ className, markClassName, wordmarkClassName, ...props }: LogoLockupProps) => (
  <div {...props} className={cn('flex items-center gap-3', className)}>
    <LogoMark className={cn('h-10 w-auto', markClassName)} />
    <LogoWordmark className={wordmarkClassName} />
  </div>
);
